/**
 * SYNCED_STORES registry (Track A · P0.7) — the single list of Zustand
 * stores that participate in the blob-sync polling loop (the production
 * BFF / Azure backend, or the legacy GitHub fallback).
 *
 * The registry is **append-only** from Phase 0 onward: to add a store to
 * the sync loop, append one alphabetised line here — no edit to
 * `use-github-sync.ts` (it fans out over this list). This is the plan's #1
 * anti-collision primitive: it replaces the hardcoded two-store
 * `Promise.all` that two tracks would otherwise both edit.
 *
 * Each entry must expose `sync(): Promise<void>` on its state.
 *
 * **What's in the poll, and what isn't (by cost):**
 *   - The blob stores (`useAssetLocker` / `useCaptures`) and the singleton
 *     content/state documents (`useWorkshopOverrides` / `useProgress` /
 *     `useLinks` / `useWorkshopSummaries`) are CHEAP to reconcile — each
 *     `sync()` is one (or a handful of) blob reads — so they ride the 15s /
 *     focus / cross-tab poll and are genuinely live across the team.
 *   - The per-workshop RECORD register stores (`useFindings` /
 *     `useOpportunities` / `useDecisions` / …) are NOT registered individually:
 *     each one's `sync()` re-lists + re-fetches all eight workshop buckets, so
 *     polling seven of them every 15s would mean ~56 list calls per tick.
 *     **E2 (done):** they're now driven by ONE registered coordinator,
 *     `useRegisterSync`, which does a single tier-wide `list?since=` per tick
 *     and fans the changed paths to all seven (≈1 list call/tick + a GET only
 *     per actually-changed record). Safe live editing relies on the E1
 *     ETag/If-Match concurrency the record stores already implement. So the
 *     coordinator is in the list below; the seven stores are wired into it via
 *     `registerSyncStores` rather than appearing here directly.
 */

import { useActions } from "@/stores/useActions";
import { useAssetLocker } from "@/stores/useAssetLocker";
import { useAssumptions } from "@/stores/useAssumptions";
import { useCaptures } from "@/stores/useCaptures";
import { useDecisions } from "@/stores/useDecisions";
import { useFacilitationNotes } from "@/stores/useFacilitationNotes";
import { useFindings } from "@/stores/useFindings";
import { useLinks } from "@/stores/useLinks";
import { useOpportunities } from "@/stores/useOpportunities";
import { useProgress } from "@/stores/useProgress";
import { useRecommendations } from "@/stores/useRecommendations";
import { registerSyncStores, useRegisterSync } from "@/stores/useRegisterSync";
import { useWorkshopContentDefaults } from "@/stores/useWorkshopContentDefaults";
import { useWorkshopOverrides } from "@/stores/useWorkshopOverrides";
import { useWorkshopSummaries } from "@/stores/useWorkshopSummaries";

// Wire the seven register stores into the E2 coordinator (it drives them via
// `ingestRemoteChanges` / `sync`, but isn't coupled to their concrete types).
registerSyncStores([
  useActions,
  useAssumptions,
  useDecisions,
  useFacilitationNotes,
  useFindings,
  useOpportunities,
  useRecommendations,
]);

/** Adapter status shared by every synced store. */
export type SyncState = "idle" | "syncing" | "synced" | "error" | "disabled";

/**
 * The sync-related slice every registry store exposes. `use-github-sync.ts`
 * needs only `sync()`; `SyncStatusPill` reads the status fields too, so the
 * registry types them once here rather than each consumer reaching into a
 * specific store.
 */
export type SyncableSlice = {
  sync: () => Promise<void>;
  syncState: SyncState;
  lastSyncedAt: number | null;
  syncError: string | null;
};

/**
 * A store whose state exposes {@link SyncableSlice} and whose changes can be
 * subscribed to (the Zustand store API surface the registry depends on). Any
 * Zustand bound store whose state extends the slice satisfies this.
 */
export type SyncableStore = {
  getState: () => SyncableSlice;
  subscribe: (listener: () => void) => () => void;
};

/**
 * Append-only, alphabetised. Add a store here to include it in the polled
 * BFF/blob reconciliation. Prerequisite: `sync(): Promise<void>` on state,
 * and a `sync()` cheap enough to run every 15s (see the cost note above).
 */
export const SYNCED_STORES: SyncableStore[] = [
  useAssetLocker,
  useCaptures,
  useLinks,
  useProgress,
  useRegisterSync,
  useWorkshopContentDefaults,
  useWorkshopOverrides,
  useWorkshopSummaries,
];
